import React, { useState, useEffect } from 'react';
import { inject, observer } from 'mobx-react';
import Select from 'react-select';
import { fetch } from '../utils/api-wrapper';
import Utils from '../utils/utils';
import MetabaseDashboard from '../components/metabase-dashboard/MetabaseDashboard';

const BI = inject('data')(observer(({ data }) => {
  const availableDashboards = [
    { id: 2, name: 'Suivi budgetaire', description: 'Vue d\'ensemble des métriques principales' },
  ];

  const [selectedDashboard, setSelectedDashboard] = useState(availableDashboards[0].id);
  const [departments, setDepartments] = useState([]);
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [defaultDepartments, setDefaultDepartments] = useState([]); // Stocker les départements par défaut
  const [params, setParams] = useState({});
  const [departmentsLoaded, setDepartmentsLoaded] = useState(false);

  const styleSelect = {
    control: (baseStyles, state) => ({
      ...baseStyles,
      border: "0 !important",
      boxShadow: state.isFocused ? "0px 0px 10px #F39800 " : "0 !important"
    })
  };

  // Charger les départements de l'utilisateur connecté
  useEffect(() => {
    if (!data.id_client || !data.id_user) {
      setDepartmentsLoaded(true);
      return;
    }
    setDepartmentsLoaded(false);
    const filters = [{id: "id_client", value: data.id_client}];
    const searchData = `?filters=${encodeURIComponent(JSON.stringify(filters ?? []))}&sorting=${encodeURIComponent(JSON.stringify([]))}&start=${encodeURIComponent(0)}&page_size=${100}`;
    fetch(`/departments/${searchData}`, "GET", {},
      (response) => {
        const fetchedDepartments = response.data.departments || [];
        
        // Si l'utilisateur est Admin ou SuperAdmin, afficher tous les départements
        const isAdmin = Utils.userHasRole(data, ["Admin", "SuperAdmin"]);
        
        let userDepartments;
        // Filtrer les départements où l'utilisateur est présent (pour la sélection par défaut)
        const userOwnDepartments = fetchedDepartments.filter((department) => {
          if (!department.users || !Array.isArray(department.users)) {
            return false;
          }
          
          // Vérifier si l'id_user de l'utilisateur connecté est dans le tableau users du département
          return department.users.some((user) => {
            const userId = user.id_user?.$oid || user.id_user || user._id?.$oid || user._id;
            return userId === data.id_user;
          });
        });
        
        // Si l'utilisateur est Admin ou SuperAdmin, afficher tous les départements
        if (isAdmin) {
          userDepartments = fetchedDepartments;
        } else {
          userDepartments = userOwnDepartments;
        }
        
        setDepartments(userDepartments);
        
        // Sélectionner par défaut les départements
        let defaultSelected;
        if (isAdmin) {
          // Pour les admins, sélectionner tous les départements par défaut
          defaultSelected = fetchedDepartments.map((department) => {
            const deptName = department.name || department.label || '';
            return {
              value: deptName,
              label: deptName
            };
          });
        } else {
          // Pour les autres utilisateurs, sélectionner seulement leurs départements
          defaultSelected = userOwnDepartments.map((department) => {
            const deptName = department.name || department.label || '';
            return {
              value: deptName,
              label: deptName
            };
          });
        }
        setDefaultDepartments(defaultSelected); // Stocker les départements par défaut
        setSelectedDepartments(defaultSelected);
        setDepartmentsLoaded(true);
      },
      (err) => {
        console.error('Erreur lors de la récupération des départements:', err);
        setDepartmentsLoaded(true);
      }
    );
  }, [data.id_client, data.id_user]);

  // Mettre à jour les params quand les départements changent
  useEffect(() => {
    // Si aucun département n'est sélectionné, utiliser les départements par défaut
    const departmentsToUse = selectedDepartments.length === 0 
      ? defaultDepartments 
      : selectedDepartments;
    
    // Extraire les noms des départements
    const departmentNames = departmentsToUse.map(dept => dept.value);
    const newParams = {
      departement: departmentNames
    };
    setParams(newParams);
  }, [selectedDepartments, defaultDepartments]);

  return (
    <div className="card rounded-lg pb-3">
      <div className="d-flex pt-4 pl-4 pr-4 pb-4 align-items-center">
        <h2>{data.t.read('bi_dashboard.title')}</h2>
      </div>

      <div className="px-4 pb-4">
        <div className="row">
          {availableDashboards.length > 0 && (
            <div className="col-md-6 mb-3">
              <label className="font-weight-bold mb-2">{data.t.read('bi_dashboard.select_dashboard')}</label>
              <select
                className="form-control"
                styles={styleSelect}
                value={selectedDashboard || ''}
                onChange={(e) => {
                  setSelectedDashboard(e.target.value ? parseInt(e.target.value) : null);
                }}
              >
                <option value="">{data.t.read('bi_dashboard.select_option')}</option>
                {availableDashboards.map((dashboard) => (
                  <option key={dashboard.id} value={dashboard.id}>
                    {dashboard.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Multiselect pour les départements */}
          {departments.length > 0 && (
            <div className="col-md-6 mb-3">
              <label className="font-weight-bold mb-2">{data.t.read('bi_dashboard.select_department')}</label>
              <Select
                isMulti
                name="departments"
                options={departments.map((department) => {
                  const deptName = department.name || department.label || '';
                  return {
                    value: deptName,
                    label: deptName
                  };
                })}
                value={selectedDepartments}
                onChange={(selectedOptions) => {
                  setSelectedDepartments(selectedOptions || []);
                }}
                placeholder={data.t.read('bi_dashboard.select_option')}
                styles={styleSelect}
                classNamePrefix="select"
              />
            </div>
          )}
        </div>
      </div>

      {/* Affichage du dashboard Metabase */}
      <div className="px-4 pb-4" style={{ minHeight: '600px' }}>
        {departmentsLoaded && !Utils.userHasRole(data, ['Admin', 'SuperAdmin']) && departments.length === 0 ? (
          <div className="alert alert-warning text-center p-5" role="alert">
            <p className="mb-0">{data.t.read('bi_dashboard.no_department_required')}</p>
          </div>
        ) : selectedDashboard ? (
          <MetabaseDashboard
            dashboardId={selectedDashboard}
            params={params}
            expirationMinutes={60}
          />
        ) : availableDashboards.length > 0 ? (
          <MetabaseDashboard
            dashboardId={availableDashboards[0].id}
            params={params}
            expirationMinutes={60}
          />
        ) : (
          <div className="text-center p-5">
            <p className="text-muted">{data.t.read('bi_dashboard.no_dashboard_available')}</p>
          </div>
        )}
      </div>
    </div>
  );
}));

export default BI;
